import { orgServiceModel } from './falUserDefinitions';
import { AccountGroup, GroupAccountMapping } from './account-group';
import { Asset } from './asset';
import { DepreciationRule } from './depreciation-rules';
import { NominalAccount } from './nominal-account';
import { FinancialYear } from './financial-years';
import { AccrualPrepaymentGroup } from './accrual-prepayment-models';
import { IKeyValue } from './group-edit-config';

export enum CompanyPostingOptions {
    Undefined = "Undefined",
    Periodic = "Periodic",
    YearEnd = "YearEnd",
    Dispose = "Dispose",
    DeleteAsset = "DeleteAsset",
    DeleteAccrual = "DeleteAccrual",
    DeleteLoan = "DeleteLoad"
}

export interface ExternalDataResult<T> {
    result: string;
    metaData: string;
    data: T;
}

export interface CompanyData {
    company: NtCompany;
    accountGroups: AccountGroup[];
    assets: Asset[];
    depreciationRules: DepreciationRule[];
    nominalMappings: NominalAccount[];
    years: FinancialYear[];
    accountMappings: GroupAccountMapping[];
    postingError: boolean;
    accrualPrepaymentGroups: AccrualPrepaymentGroup[];
    accrualPrepaymentMappings: GroupAccountMapping[];
}

export class NtCompany {
    id: number;
    safeId: string;
    billingOrganisationId: string;
    billingOrganisation: any;
    serviceName: string;
    serviceKey: string;
    subscribedServiceId: number;
    active: boolean;
    createdOn: Date;
    createdBy: string;
    lastAccessedDate: Date;
    lastAccessedBy: string;
    companyName: string;
    processingStartDate: Date;
    financialYearEnd: Date;
    defaultReportingPeriod: string;
    region: string;
    defaultCurrency: string;
    externalDataSource: ExternalDataSource;
    enableAutomatedProcessing: boolean;
    lastProcessingDate: Date;
    nextProcessingDate: Date;
    awaitingYearEnd: boolean;
    yearEndDraftComplete: boolean;
    assetWriteOffLessThan: number;
    users: NTUser[] = [];
    subscribedService: orgServiceModel;
    externalDataEnabled = false;
    pendingDeletion = false;
    pendingDeletionDate: Date;
    deleted = false;
    deletedDate: Date;
    automationEnableReporting: boolean;
    automationEnableTransactionRefresh: boolean;
    automationEnableAssetCreation: boolean;
    autoAssetCreationThreshold: number;
    lastTransactionRefresh: Date;
    hasPendingTransactions: boolean;
    enableClass: boolean;
    defaultDepreciationStart: string;
    useNtJournalDocNumber: boolean;
    ntJournalDocNumberPrefix: string;
    farDbVersion: string;
    useAutoAssetReference: boolean;
    autoAssetReferencePrefix: string;
    autoAssetReferenceNext: number;
    useProcessingDateForJournalEntryDate: boolean;
    useProviderLocation: boolean;
    isExternalDataEnabled = false;
    condenseJournalLinesAccrualPrepayment: boolean;
    condenseJournalLinesAssetRegister: boolean;
    assetRegisterCalculationResolution: string;
    accrualPrepaymentCalculationResolution: string;
    useYearEndJournals: boolean;
    useSameBalanceSheetAccountsForDisposals: boolean;
    companyVersion: number;
    perpetualFinancialYear: boolean;
    enableAssetTaxDepreciation: boolean;
    isNew = false;
    featureList: CompanyFeature[] = [];

    public static GetProviderClassLabel(provider: string): string{
        if(provider === 'QuickBooks'){
            return 'Enable Class';
        }else if(provider === 'SageCloud'){
            return 'Enable Deptartment';
        }else if(provider === 'Xero'){
            return 'Enable Tracking Cat 1';
        }else{
            return 'Category';
        }
    }

    public static GetProviderClassColName(provider: string): string{
        if(provider === 'QuickBooks'){
            return 'Class';
        }else if(provider === 'SageCloud'){
            return 'Dept';
        }else if(provider === 'Xero'){
            return 'Track Cat 1';
        }else{
            return 'Category';
        }
    }

    public static GetProviderClassTitle(provider: string): string{
        if(provider === 'QuickBooks'){
            return 'Class';
        }else if(provider === 'SageCloud'){
            return 'Deptartment';
        }else if(provider === 'Xero'){
            return 'Tracking Category 1';
        }else{
            return 'Category';
        }
    }

    public static GetProviderLocationLabel(provider: string, useProviderLocation): string{
        if(provider === 'QuickBooks'){
            return 'Enable Provider Location';
        }else if(provider === 'SageCloud'){
            return 'Enable Cost Centre';
        }else if(provider === 'Xero'){
            return 'Enable Tracking Cat 2';
        }else{
            return 'Location';
        }
    }

    public static GetProviderLocationColName(provider: string, useProviderLocation): string{
        if(useProviderLocation){
            if(provider === 'QuickBooks'){
                return 'Location';
            }else if(provider === 'SageCloud'){
                return 'Cost Centre';
            }else if(provider === 'Xero'){
                return 'Track Cat 2';
            }else{
                return 'N/A';
            }
        }else{
            return "Location";
        }
    }

    public static GetProviderLocationTitle(provider: string, useProviderLocation): string{
        if(useProviderLocation){
            if(provider === 'QuickBooks'){
                return 'Location';
            }else if(provider === 'SageCloud'){
                return 'Cost Centre';
            }else if(provider === 'Xero'){
                return 'Tracking Category 2';
            }else{
                return 'N/A';
            }
        }else{
            return "Location";
        }
    }

    public static IsAccrualsPrepaymentsEnabled(c: NtCompany): boolean{
        return c.featureList.filter(x=>x.key === 'AccrualsPrepayments').length > 0;
    }

    public static IsLoansEnabled(c: NtCompany): boolean {
        return c.featureList.filter(x=>x.key === 'Loans').length > 0;
    }

}

export const GetDefaultCurrencyList = (): IKeyValue[] => {
    return [
        {key: 'GBP', value: 'GBP'}, 
        {key: 'USD', value: 'USD'}, 
        {key: 'EUR', value: 'EUR'}, 
        {key: 'CAD', value: 'CAD'}, 
        {key: 'AUD', value: 'AUD'},
        {key: 'NZD', value: 'NZD'},
        {key: 'INR', value: 'INR'}, 
        {key: 'ZAR', value: 'ZAR'},
        {key: 'IDR', value: 'IDR'},
        {key: 'AWG', value: 'AWG'},
    ];
}

export interface ProviderTrackingData {
    class?: string[];
    location?: string[];
}

export interface ProviderTrackingDataProcessed {
    class?: string[];
    location?: any[];
}

export class ExternalDataSource {
    dataSource: string;
    externalReference: string;
    authToken: string;
    refreshToken: string;
    refreshTokenExpiry: Date;
    lastAccessed: Date;
    lastRefreshed: Date;
    tokenExpiry: Date;
    connectState: string;
    error: boolean;
    reconnectRequired: boolean;
    modified: boolean;
    connectionDate: Date;
    nextRefreshDate: Date;
    name: string;
    state: string;
}

export class ProviderConnectRequest{
    providerName: string;
    state: string;
    purpose: string;
    url: string;
    companyId?: string;
}

export class NTUser {
    ntCompanyId: number;
    ntCompanySafeId: string;
    userId: string;
    name: string;
    email: string;
    role: string;
    createdOn: Date;
    createdBy: string;
    modifiedOn: Date;
    modifiedBy: string;
    userDefault: boolean;
    safeId: string;
}

export class NTFeature{
    ntCompanySafeId: string;
    featureKey: string;
    featureName: string;
    active: string;
    activatedOn: Date;
    deactivatedOn: Date;
    modifiable: boolean;
    notes: string;
    calculationResolution: string;
}

export class ConnectionRequestObject {
    url: string;
    state: string;
    dataSource: string;
}

export class OrgUserCompanyModel {
    organisationId: string;
    userId: string;
    userName: string;
    userEmail: string;
    companyRoles: CompanyRole[] = [];
}

export class CompanyRole {
    companyId: string;
    role: string;
    companyName: string;
}

export class CompanyFeature{
    key: string;
    name: string;
    calculationResolution: string;
}


export const MAT_UK_DATE_FORMATS = {
    parse: {
        dateInput: 'DD/MM/YYYY'
    },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'L',
        monthYearA11yLabel: 'MM YYYY'
    }
};

export interface IUpdatedDateRange {
    startDate: Date;
    endDate: Date;
    yearId: number;
}
